import type { SurfDeck } from "@vida/surf-core";
import {
  chromaKeyHexForLayer,
  getTextRasterAssetKey,
  needsRasterChromaKeyPrep,
} from "@vida/surf-core";

export type SurfDeckAssetItem = {
  slideId: string;
  layerId?: string;
  key: string;
  chromaKeyPrep: boolean;
  chromaHex?: string;
};

/** Collect s3 keys for slide backgrounds, image layers, and text raster assets. */
export function collectSurfDeckAssetItems(deck: SurfDeck): SurfDeckAssetItem[] {
  const items: SurfDeckAssetItem[] = [];
  for (const slide of deck.slides) {
    const bg = slide.background.s3Key?.trim();
    if (bg) items.push({ slideId: slide.id, key: bg, chromaKeyPrep: false });
    for (const layer of slide.layers) {
      if (layer.type === "image") {
        const key = layer.content?.trim();
        if (key && key.includes("/")) {
          const chromaKeyPrep = needsRasterChromaKeyPrep(layer);
          items.push({
            slideId: slide.id,
            layerId: layer.id,
            key,
            chromaKeyPrep,
            chromaHex: chromaKeyPrep ? chromaKeyHexForLayer(layer, deck.theme) : undefined,
          });
        }
        continue;
      }
      if (layer.type === "text") {
        const rasterKey = getTextRasterAssetKey(layer);
        if (rasterKey) {
          const chromaKeyPrep = needsRasterChromaKeyPrep(layer);
          items.push({
            slideId: slide.id,
            layerId: layer.id,
            key: rasterKey,
            chromaKeyPrep,
            chromaHex: chromaKeyPrep ? chromaKeyHexForLayer(layer, deck.theme) : undefined,
          });
        }
      }
    }
  }
  return items;
}

export function surfDeckAssetFingerprint(deck: SurfDeck): string {
  return [
    deck.theme.previewS3Key?.trim() ?? "",
    ...collectSurfDeckAssetItems(deck).map(
      (item) =>
        `${item.slideId}\0${item.layerId ?? ""}\0${item.key}\0${item.chromaKeyPrep ? "c" : "i"}\0${item.chromaHex ?? ""}`,
    ),
  ]
    .sort()
    .join("\n");
}
